<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import PageHeader from '../components/PageHeader.vue';
import StatusBadge from '../components/StatusBadge.vue';
import VersionHistory from '../components/VersionHistory.vue';
import { getApiErrorMessage } from '../utils';

const { t } = useI18n();

const knowledgeBases = ref([]);
const availableCities = ref([]);
const selectedScope = ref('global');
const knowledgeBase = ref(null);
const selectedVersionId = ref(null);
const expandedEntries = ref(new Set());
const entryForms = ref({});
const newEntry = ref({ title: '', content: '' });
const isLoading = ref(false);
const isMutating = ref(false);
const savingEntryId = ref(null);

const scopes = computed(() => [
  { value: 'global', label: t('AI_REVIEWS.KNOWLEDGE_BASES.GLOBAL') },
  ...availableCities.value
    .filter(city => city.toLocaleLowerCase() !== 'другой')
    .map(city => ({
      value: `city:${city}`,
      label: t('AI_REVIEWS.KNOWLEDGE_BASES.CITY', { city }),
    })),
]);

const selectedCity = computed(() =>
  selectedScope.value.startsWith('city:') ? selectedScope.value.slice(5) : null
);

const selectedBaseSummary = computed(() =>
  knowledgeBases.value.find(base => {
    if (!selectedCity.value) return base.scope === 'global';
    return (
      base.city?.toLocaleLowerCase() === selectedCity.value.toLocaleLowerCase()
    );
  })
);

const selectedVersion = computed(() =>
  knowledgeBase.value?.versions?.find(
    version => Number(version.id) === Number(selectedVersionId.value)
  )
);

const draftVersion = computed(() =>
  knowledgeBase.value?.versions?.find(version => version.state === 'draft')
);

const isDraft = computed(() => selectedVersion.value?.state === 'draft');

watch(selectedVersion, version => {
  entryForms.value = Object.fromEntries(
    (version?.entries || []).map(entry => [
      entry.id,
      { title: entry.title, content: entry.content },
    ])
  );
  expandedEntries.value = new Set();
});

const selectInitialVersion = preferredId => {
  const versions = knowledgeBase.value?.versions || [];
  const preferred = versions.find(
    version => Number(version.id) === Number(preferredId)
  );
  selectedVersionId.value =
    preferred?.id ||
    versions.find(version => version.state === 'draft')?.id ||
    knowledgeBase.value?.active_version?.id ||
    versions[0]?.id ||
    null;
};

const loadSelectedBase = async preferredVersionId => {
  const summary = selectedBaseSummary.value;
  if (!summary) {
    knowledgeBase.value = null;
    selectedVersionId.value = null;
    return;
  }

  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getKnowledgeBase(summary.id);
    knowledgeBase.value = data;
    selectInitialVersion(preferredVersionId);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
};

const loadKnowledgeBases = async () => {
  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getKnowledgeBases();
    knowledgeBases.value = data.payload || [];
    availableCities.value = data.available_cities || [];
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
  await loadSelectedBase();
};

watch(selectedScope, () => loadSelectedBase());

const refreshIndexAndBase = async preferredVersionId => {
  const { data } = await aiReviewsAPI.getKnowledgeBases();
  knowledgeBases.value = data.payload || [];
  availableCities.value = data.available_cities || [];
  await loadSelectedBase(preferredVersionId);
};

const createKnowledgeBase = async () => {
  try {
    isMutating.value = true;
    const { data } = await aiReviewsAPI.createKnowledgeBase({
      name: selectedCity.value
        ? t('AI_REVIEWS.KNOWLEDGE_BASES.NAME_CITY', {
            city: selectedCity.value,
          })
        : t('AI_REVIEWS.KNOWLEDGE_BASES.NAME_GLOBAL'),
      city: selectedCity.value,
      entries: [],
    });
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.CREATE_SUCCESS'));
    await refreshIndexAndBase(
      data.versions?.find(version => version.state === 'draft')?.id
    );
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isMutating.value = false;
  }
};

const createDraft = async () => {
  try {
    isMutating.value = true;
    const { data } = await aiReviewsAPI.createKnowledgeBaseVersion(
      knowledgeBase.value.id,
      selectedVersion.value?.id
    );
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.DRAFT_SUCCESS'));
    await loadSelectedBase(data.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isMutating.value = false;
  }
};

const publishVersion = async () => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('AI_REVIEWS.KNOWLEDGE_BASES.PUBLISH_CONFIRM', {
        version: selectedVersion.value.version_number,
      })
    )
  ) {
    return;
  }

  try {
    isMutating.value = true;
    const { data } = await aiReviewsAPI.publishKnowledgeBaseVersion(
      selectedVersion.value.id
    );
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.PUBLISH_SUCCESS'));
    await refreshIndexAndBase(data.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isMutating.value = false;
  }
};

const replaceVersion = version => {
  const index = knowledgeBase.value.versions.findIndex(
    item => Number(item.id) === Number(version.id)
  );
  if (index >= 0) knowledgeBase.value.versions[index] = version;
  selectedVersionId.value = version.id;
};

const toggleEntry = entryId => {
  const next = new Set(expandedEntries.value);
  if (next.has(entryId)) next.delete(entryId);
  else next.add(entryId);
  expandedEntries.value = next;
};

const saveEntry = async entry => {
  try {
    savingEntryId.value = entry.id;
    const { data } = await aiReviewsAPI.updateKnowledgeBaseEntry(
      entry.id,
      entryForms.value[entry.id]
    );
    replaceVersion(data);
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_SAVED'));
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    savingEntryId.value = null;
  }
};

const addEntry = async () => {
  try {
    isMutating.value = true;
    const { data } = await aiReviewsAPI.createKnowledgeBaseEntry(
      selectedVersion.value.id,
      {
        ...newEntry.value,
        position: selectedVersion.value.entries.length,
      }
    );
    replaceVersion(data);
    newEntry.value = { title: '', content: '' };
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_CREATED'));
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isMutating.value = false;
  }
};

const deleteEntry = async entry => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('AI_REVIEWS.KNOWLEDGE_BASES.DELETE_CONFIRM', {
        title: entry.title,
      })
    )
  ) {
    return;
  }

  try {
    savingEntryId.value = entry.id;
    await aiReviewsAPI.deleteKnowledgeBaseEntry(entry.id);
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_DELETED'));
    await loadSelectedBase(selectedVersion.value.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    savingEntryId.value = null;
  }
};

const moveEntry = async (index, offset) => {
  const targetIndex = index + offset;
  const entries = [...selectedVersion.value.entries];
  if (targetIndex < 0 || targetIndex >= entries.length) return;
  const [entry] = entries.splice(index, 1);
  entries.splice(targetIndex, 0, entry);

  try {
    savingEntryId.value = entry.id;
    const { data } = await aiReviewsAPI.reorderKnowledgeBaseEntries(
      selectedVersion.value.id,
      entries.map(item => item.id)
    );
    replaceVersion(data);
    useAlert(t('AI_REVIEWS.KNOWLEDGE_BASES.REORDERED'));
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    savingEntryId.value = null;
  }
};

onMounted(loadKnowledgeBases);
</script>

<template>
  <PageHeader
    :title="t('AI_REVIEWS.KNOWLEDGE_BASES.TITLE')"
    :description="t('AI_REVIEWS.KNOWLEDGE_BASES.DESCRIPTION')"
  />

  <div class="mb-5">
    <label
      for="ai-review-kb-scope"
      class="block mb-1 text-sm font-medium text-n-slate-12"
    >
      {{ t('AI_REVIEWS.KNOWLEDGE_BASES.SCOPE_SELECTOR') }}
    </label>
    <select
      id="ai-review-kb-scope"
      v-model="selectedScope"
      class="w-full max-w-md h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12"
    >
      <option v-for="scope in scopes" :key="scope.value" :value="scope.value">
        {{ scope.label }}
      </option>
    </select>
  </div>

  <div
    v-if="isLoading && !knowledgeBase"
    class="p-10 text-center text-n-slate-11"
  >
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>

  <section
    v-else-if="!knowledgeBase"
    class="flex flex-col items-center gap-4 p-10 text-center border border-dashed rounded-xl border-n-weak"
  >
    <p class="mb-0 text-sm text-n-slate-11">
      {{ t('AI_REVIEWS.KNOWLEDGE_BASES.NOT_CREATED') }}
    </p>
    <Button
      :label="t('AI_REVIEWS.KNOWLEDGE_BASES.CREATE_BASE')"
      icon="i-lucide-plus"
      :is-loading="isMutating"
      @click="createKnowledgeBase"
    />
  </section>

  <div v-else class="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
    <VersionHistory
      :title="t('AI_REVIEWS.KNOWLEDGE_BASES.VERSION_HISTORY')"
      :versions="knowledgeBase.versions"
      :selected-id="selectedVersionId"
      @select="selectedVersionId = $event"
    />

    <section
      v-if="selectedVersion"
      class="p-5 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div class="flex items-center gap-2">
          <h3 class="mb-0 text-lg font-semibold text-n-slate-12">
            {{
              t('AI_REVIEWS.COMMON.VERSION', {
                version: selectedVersion.version_number,
              })
            }}
          </h3>
          <StatusBadge :status="selectedVersion.state" />
        </div>
        <div class="flex gap-2">
          <Button
            v-if="!draftVersion"
            :label="t('AI_REVIEWS.KNOWLEDGE_BASES.CREATE_DRAFT')"
            icon="i-lucide-copy-plus"
            color="slate"
            variant="faded"
            :is-loading="isMutating"
            @click="createDraft"
          />
          <Button
            v-if="isDraft"
            :label="t('AI_REVIEWS.COMMON.PUBLISH')"
            :is-loading="isMutating"
            @click="publishVersion"
          />
        </div>
      </div>

      <p
        class="p-3 mb-5 text-sm rounded-lg"
        :class="
          isDraft
            ? 'bg-n-amber-3 text-n-amber-11'
            : 'bg-n-alpha-1 text-n-slate-11'
        "
      >
        {{
          isDraft
            ? t('AI_REVIEWS.KNOWLEDGE_BASES.DRAFT_BANNER')
            : t('AI_REVIEWS.KNOWLEDGE_BASES.READ_ONLY_NOTICE')
        }}
      </p>

      <h4 class="mb-3 text-base font-semibold text-n-slate-12">
        {{ t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRIES') }}
      </h4>

      <div
        v-if="!selectedVersion.entries.length"
        class="p-6 mb-4 text-sm text-center border border-dashed rounded-lg border-n-weak text-n-slate-11"
      >
        {{ t('AI_REVIEWS.KNOWLEDGE_BASES.EMPTY_ENTRIES') }}
      </div>

      <div class="flex flex-col gap-2">
        <article
          v-for="(entry, index) in selectedVersion.entries"
          :key="entry.id"
          class="border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <div class="flex items-center gap-2 p-3">
            <button
              type="button"
              class="flex items-center flex-1 min-w-0 gap-2 text-left text-sm font-medium text-n-slate-12"
              :aria-expanded="expandedEntries.has(entry.id)"
              :aria-label="
                t(
                  expandedEntries.has(entry.id)
                    ? 'AI_REVIEWS.KNOWLEDGE_BASES.COLLAPSE_ENTRY'
                    : 'AI_REVIEWS.KNOWLEDGE_BASES.EXPAND_ENTRY',
                  { title: entry.title }
                )
              "
              @click="toggleEntry(entry.id)"
            >
              <span
                class="flex-shrink-0 size-4"
                :class="
                  expandedEntries.has(entry.id)
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
              />
              <span class="truncate">{{ entry.title }}</span>
            </button>
            <template v-if="isDraft">
              <Button
                icon="i-lucide-arrow-up"
                color="slate"
                variant="ghost"
                size="sm"
                :aria-label="
                  t('AI_REVIEWS.KNOWLEDGE_BASES.MOVE_UP', {
                    title: entry.title,
                  })
                "
                :disabled="index === 0 || savingEntryId === entry.id"
                @click="moveEntry(index, -1)"
              />
              <Button
                icon="i-lucide-arrow-down"
                color="slate"
                variant="ghost"
                size="sm"
                :aria-label="
                  t('AI_REVIEWS.KNOWLEDGE_BASES.MOVE_DOWN', {
                    title: entry.title,
                  })
                "
                :disabled="
                  index === selectedVersion.entries.length - 1 ||
                  savingEntryId === entry.id
                "
                @click="moveEntry(index, 1)"
              />
              <Button
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="sm"
                :aria-label="t('AI_REVIEWS.KNOWLEDGE_BASES.DELETE_ENTRY')"
                :is-loading="savingEntryId === entry.id"
                @click="deleteEntry(entry)"
              />
            </template>
          </div>

          <div
            v-if="expandedEntries.has(entry.id)"
            class="grid gap-4 p-4 border-t border-n-weak"
          >
            <template v-if="isDraft">
              <Input
                :id="`kb-entry-title-${entry.id}`"
                v-model="entryForms[entry.id].title"
                :label="t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_TITLE')"
              />
              <Textarea
                :id="`kb-entry-content-${entry.id}`"
                v-model="entryForms[entry.id].content"
                :label="t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_CONTENT')"
                resize
                custom-text-area-class="min-h-40"
              />
              <div class="flex justify-end">
                <Button
                  :label="t('AI_REVIEWS.KNOWLEDGE_BASES.SAVE_ENTRY')"
                  :is-loading="savingEntryId === entry.id"
                  :disabled="!entryForms[entry.id].title.trim()"
                  @click="saveEntry(entry)"
                />
              </div>
            </template>
            <template v-else>
              <h5 class="mb-0 text-sm font-semibold text-n-slate-12">
                {{ entry.title }}
              </h5>
              <p class="mb-0 text-sm whitespace-pre-wrap text-n-slate-11">
                {{ entry.content }}
              </p>
            </template>
          </div>
        </article>
      </div>

      <form
        v-if="isDraft"
        class="grid gap-4 p-4 mt-5 border border-dashed rounded-lg border-n-weak"
        @submit.prevent="addEntry"
      >
        <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ t('AI_REVIEWS.KNOWLEDGE_BASES.ADD_ENTRY') }}
        </h4>
        <Input
          id="ai-review-new-entry-title"
          v-model="newEntry.title"
          :label="t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_TITLE')"
        />
        <Textarea
          id="ai-review-new-entry-content"
          v-model="newEntry.content"
          :label="t('AI_REVIEWS.KNOWLEDGE_BASES.ENTRY_CONTENT')"
          resize
          custom-text-area-class="min-h-32"
        />
        <div class="flex justify-end">
          <Button
            type="submit"
            :label="t('AI_REVIEWS.KNOWLEDGE_BASES.ADD_ENTRY')"
            icon="i-lucide-plus"
            :is-loading="isMutating"
            :disabled="!newEntry.title.trim()"
          />
        </div>
      </form>
    </section>
  </div>
</template>
